import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getUploadedPhotos } from '../../Services/arndAPI';
import { formatInZone } from '../../Utils/eventTime';
import styles from './Styles/PhotoUploader.module.css';
import { PARALLEL, readLedger, useUploadQueue } from './uploadQueue';

/**
 * SCRUM-415: push photos to the event from the browser. The run itself is
 * an UploadQueue (uploadQueue.js) that outlives this panel — collapsing it
 * or leaving the ops page never stops a run (SCRUM-427). This component
 * adds files, shows progress and the two accounting lists: the event's live
 * photos by name (from the API) and this browser's per-event ledger.
 */
export { PARALLEL, RETRIES, remoteName } from './uploadQueue';

const fmtBytes = (n) => {
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)} GB`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)} MB`;
  return `${Math.round(n / 1e3)} KB`;
};
/** Every File under a dropped folder (recursive) or a plain dropped file —
 * the DataTransfer entry API is the only way a drop exposes directories. */
async function filesFromDrop(dataTransfer) {
  const entries = Array.from(dataTransfer.items || [])
    .map((it) => (it.kind === 'file' && it.webkitGetAsEntry ? it.webkitGetAsEntry() : null))
    .filter(Boolean);
  if (entries.length === 0) return Array.from(dataTransfer.files || []);
  const out = [];
  const readDir = (dir) => new Promise((resolve, reject) => {
    const reader = dir.createReader();
    const all = [];
    const next = () => reader.readEntries((batch) => {
      if (batch.length === 0) return resolve(all);
      all.push(...batch);
      next(); // readEntries returns at most ~100 per call
    }, reject);
    next();
  });
  const walk = async (entry) => {
    if (entry.isFile) {
      out.push(await new Promise((resolve, reject) => entry.file(resolve, reject)));
    } else if (entry.isDirectory) {
      for (const child of await readDir(entry)) await walk(child);
    }
  };
  for (const e of entries) await walk(e);
  return out;
}

export default function PhotoUploader({ eventId, timezone }) {
  const [server, setServer] = useState(null); // { count, files, names:Set }
  const [serverOpen, setServerOpen] = useState(false);
  const [ledger, setLedger] = useState(() => readLedger(eventId));
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [listOpen, setListOpen] = useState(false); // collapsed: only what is moving or broken
  const folderRef = useRef(null);

  const refreshServer = useCallback(async () => {
    try {
      const data = await getUploadedPhotos(eventId);
      setServer({ ...data, names: new Set(data.files.map((f) => f.name)) });
    } catch (e) {
      setServer((s) => ({ ...(s || { count: 0, files: [], names: new Set() }), error: e.message }));
    }
  }, [eventId]);
  useEffect(() => { refreshServer(); }, [refreshServer]);

  // The queue re-renders us on every tick; when a run ends, re-read the
  // event's list. The ledger is re-read on each non-progress change (a
  // finished file appended to it).
  const queue = useUploadQueue(eventId, refreshServer);
  useEffect(() => queue.subscribe((kind) => { if (kind !== 'progress') setLedger(readLedger(eventId)); }), [queue, eventId]);
  useEffect(() => { queue.serverNames = server?.names ?? null; }, [queue, server]);

  const running = queue.running;
  const addFiles = (fileList) => queue.addFiles(fileList);
  const start = () => queue.start();
  const stop = () => queue.stop();
  const retryFailed = () => queue.retryFailed();
  const clearDone = () => queue.clearDone();

  const list = queue.items;
  const count = (s) => list.filter((i) => i.status === s).length;
  const pushed = count('pushed');
  const dupes = count('duplicate');
  const failed = count('failed');
  const pending = list.filter((i) => ['queued', 'uploading', 'retrying'].includes(i.status));
  const totalBytes = list.reduce((a, i) => a + i.size, 0);
  const doneBytes = list.reduce((a, i) => a + (['pushed', 'duplicate', 'skipped'].includes(i.status) ? i.size : i.size * (i.progress || 0)), 0);
  const overall = totalBytes ? doneBytes / totalBytes : 0;
  const ledgerPushed = ledger.filter((l) => l.status === 'pushed').length;
  const fmtAt = (d) => (timezone ? formatInZone(d, timezone, 'time') : new Date(d).toLocaleTimeString());

  return (
    <div className={styles.uploader}>
      <div className={styles.toolbar}>
        <input
          ref={folderRef}
          type="file"
          multiple
          webkitdirectory=""
          directory=""
          style={{ display: 'none' }}
          onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
        />
        <button className={styles.btn} onClick={() => folderRef.current?.click()}>Add folder…</button>
        {!running && pending.length > 0 && (
          <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={start}>
            Push {pending.length} file{pending.length === 1 ? '' : 's'} ({fmtBytes(pending.reduce((a, i) => a + i.size, 0))})
          </button>
        )}
        {running && <button className={`${styles.btn} ${styles.btnDanger}`} onClick={stop}>Stop</button>}
        {!running && failed > 0 && <button className={styles.btn} onClick={retryFailed}>Retry {failed} failed</button>}
        {!running && (pushed > 0 || dupes > 0) && <button className={styles.btnGhost} onClick={clearDone}>Clear finished</button>}
      </div>

      <div
        className={`${styles.drop} ${list.length ? styles.dropCompact : ''}`}
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={(e) => { e.preventDefault(); filesFromDrop(e.dataTransfer).then(addFiles); }}
      >
        {list.length === 0
          ? `Drop photos or folders here, or Add folder… (subfolders included). ${PARALLEL} at a time, retried until they land, duplicates by name are skipped.`
          : `Drop more files here`}
      </div>

      {list.length > 0 && (
        <>
          <div className={styles.summary}>
            <span className={styles.summaryStrong}>{pushed} pushed</span>
            <span>· {dupes} duplicate</span>
            <span className={failed ? styles.bad : ''}>· {failed} failed</span>
            <span>· {pending.length} to go</span>
            <span className={styles.muted}>· {fmtBytes(doneBytes)} / {fmtBytes(totalBytes)}</span>
            {queue.ignored > 0 && (
              <span className={styles.muted} title="macOS ._ sidecars, hidden files and non-images are never uploaded (SCRUM-418)">
                · {queue.ignored} ignored (not photos)
              </span>
            )}
            {running && <span className={styles.live}>● {queue.inFlight} in flight</span>}
          </div>
          <div className={styles.barOuter} title={`${Math.round(overall * 100)}%`}>
            <div className={styles.barInner} style={{ width: `${overall * 100}%` }} />
          </div>
          <button className={styles.linkBtn} onClick={() => setListOpen((o) => !o)}>
            {listOpen ? '▾ all files' : `▸ ${list.length} files (showing in-flight and failed only)`}
          </button>
          <div className={styles.rows}>
            {(listOpen ? list : list.filter((i) => ['uploading', 'retrying', 'failed'].includes(i.status))).map((i) => (
              <div key={i.id} className={`${styles.row} ${styles[`st_${i.status}`] || ''}`}>
                <span className={styles.rowName} title={i.file.name}>{i.name}</span>
                <span className={styles.rowSize}>{fmtBytes(i.size)}</span>
                <span className={styles.rowBar}>
                  {(i.status === 'uploading' || i.status === 'pushed') && (
                    <span className={styles.rowBarOuter}><span className={styles.rowBarInner} style={{ width: `${(i.progress || 0) * 100}%` }} /></span>
                  )}
                </span>
                <span className={styles.rowStatus}>
                  {i.status === 'queued' && 'queued'}
                  {i.status === 'uploading' && `${Math.round((i.progress || 0) * 100)}%${i.attempts > 1 ? ` (try ${i.attempts})` : ''}`}
                  {i.status === 'retrying' && <span className={styles.warn} title={i.error}>{i.error}</span>}
                  {i.status === 'pushed' && <span className={styles.ok}>✓ pushed {fmtAt(i.at)}</span>}
                  {i.status === 'duplicate' && <span className={styles.muted}>skipped · {i.error}</span>}
                  {i.status === 'skipped' && <span className={styles.muted}>not a photo · {i.error}</span>}
                  {i.status === 'failed' && <span className={styles.bad} title={i.error}>✗ {i.error}</span>}
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      <div className={styles.foot}>
        <button className={styles.linkBtn} onClick={() => setServerOpen((o) => !o)}>
          {serverOpen ? '▾' : '▸'} In the event: {server ? `${server.count} photo${server.count === 1 ? '' : 's'}` : '…'}
          {server?.error && <span className={styles.bad}> ({server.error})</span>}
        </button>
        {server && (
          <span title="Files in the event's folder on the FTP box (everything ever landed there, partial uploads included)">
            · FTP folder: {server.ftp ? `${server.ftp.count} file${server.ftp.count === 1 ? '' : 's'}` : 'not reachable'}
            {server.ftp?.notRecorded?.length > 0 && (
              <span className={styles.warn} title={server.ftp.notRecorded.join('\n')}> · {server.ftp.notRecorded.length} landed but not in the event</span>
            )}
          </span>
        )}
        <button className={styles.linkBtn} onClick={refreshServer} title="Re-read the event's photo list">↻</button>
        <button className={styles.linkBtn} onClick={() => setLedgerOpen((o) => !o)}>
          {ledgerOpen ? '▾' : '▸'} This browser: {ledgerPushed} pushed, {ledger.length - ledgerPushed} skipped/failed
        </button>
      </div>
      {serverOpen && server && (
        <div className={styles.serverList}>
          {server.files.map((f) => (
            <div key={`${f.name}-${f.at}`} className={styles.serverRow}>
              <span className={styles.rowName}>{f.name}</span>
              <span className={styles.muted}>{fmtAt(f.at)}</span>
            </div>
          ))}
        </div>
      )}
      {ledgerOpen && (
        <div className={styles.serverList}>
          {[...ledger].reverse().map((l, n) => (
            <div key={`${l.name}-${l.at}-${n}`} className={styles.serverRow}>
              <span className={styles.rowName}>{l.name}</span>
              <span className={l.status === 'pushed' ? styles.ok : l.status === 'failed' ? styles.bad : styles.muted}>{l.status}{l.error ? ` · ${l.error}` : ''}</span>
              <span className={styles.muted}>{fmtAt(l.at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
